import React, { useEffect, useState } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { Form, Button, Col } from 'react-bootstrap';
import { findLastIndex, get } from 'lodash';
import Plus from 'mdi-react/PlusIcon';
import Minus from 'mdi-react/CloseIcon';
import classNames from 'classnames';

import { trims } from 'utils';
import { useModal } from 'modules/shared/hooks';
import { Input } from 'modules/shared/Forms';
import ModalWrapper from 'modules/shared/ModalWrapper';
import { INITIAL_VALUE } from 'modules/admin/containers/BranchEditing/hooks/usePrepareBranch';
import styles from './MultyInput.module.scss';

type Props = {
  label: string;
  name: string;
  value: any;
  onChange: (e: React.ChangeEvent<any>) => void;
  paragraph?: boolean;
  isRequiredAtStart?: boolean;
};

const MultyInput: React.FC<Props> = ({ label, name, value, onChange, paragraph, isRequiredAtStart = false }) => {
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const [removeIndex, setRemoveIndex] = useState<number>(0);
  const [arrDoubles, setArrDoubles] = useState<number[]>([]);
  const { isShow, openModal, closeModal } = useModal();
  const isPostSecurity = name.includes('securityGates');

  const checkDoubles = () => {
    const uniqValues = [] as string[];
    const doubles = [] as number[];

    value.forEach((doc: any, index: number) => {
      if (doc.isDeleted) return;
      const str = trims(`${doc.name}`.toLowerCase());

      if (uniqValues.includes(str)) {
        doubles.push(index);
      } else {
        uniqValues.push(str);
      }
    });

    doubles.forEach((arr: number) => {
      setFieldTouched(`${name}[${arr}].name`);
    });

    setArrDoubles(doubles);
  };

  const removeHandler = (index: number) => {
    if (name && value[index].name === '') {
      value.splice(index, 1);
      setFieldValue(`${name}`, value);
      setRemoveIndex(0);
      return;
    }
    if (name && value[index].name !== '') {
      setFieldValue(`${name}[${index}].isDeleted`, true);
      closeModal();
      checkDoubles();

      setRemoveIndex(0);
      return;
    }
    if (name === 'securityGates' && value[index].name === '') {
      value.splice(index, 1);
      setFieldValue(`${name}`, value);
      closeModal();
      checkDoubles();
      setRemoveIndex(0);
      return;
    }
    if (name === 'securityGates' && value[index].name !== '') {
      setFieldValue(`${name}[${index}].isDeleted`, true);
      closeModal();
      checkDoubles();

      setRemoveIndex(0);
    }
  };

  useEffect(() => {
    if (value) checkDoubles();
  }, [value]);

  return (
    <Form.Group className="d-flex flex-row align-items-start">
      <FieldArray
        name={name}
        render={(arrayHelpers) => {
          const addHandler = () => {
            if (isPostSecurity) {
              const initValue = get(INITIAL_VALUE, 'securityGates[0]');

              return arrayHelpers.push(initValue);
            }

            arrayHelpers.push({ name: '', isDeleted: false });
          };

          return (
            <>
              {paragraph && <span className={styles.paragraph} />}
              <Form.Label className={`col-4 pl-4 ml-1 ${paragraph ? 'pr-0' : 'mr-7px'} position-relative`}>
                <Button
                  variant="success"
                  className={classNames(styles.plus_btn, 'rounded-circle')}
                  onClick={addHandler}
                  disabled={
                    value.length !== 0 && // @ts-ignore
                    value?.[findLastIndex(value)]?.name === ''
                  }
                >
                  <Plus size="14" />
                </Button>
                <div style={{ marginLeft: '20px' }}>
                  {label}
                  {isRequiredAtStart && <em className="text-danger mx-1">*</em>}:
                </div>
              </Form.Label>
              <div
                className={classNames('d-flex flex-column w-100 position-relative', paragraph ? styles.transfLeft : '')}
              >
                {value?.length > 0 &&
                  value.map((v: any, index: number) => {
                    const openDialog = () => {
                      setRemoveIndex(index);
                      openModal();
                    };

                    const onRemove = () => {
                      removeHandler(index);
                    };

                    const isExist = !!arrDoubles.length && !!arrDoubles.find((a: number) => a === index);

                    if (v.isDeleted) return;

                    return (
                      <div
                        key={index}
                        className={classNames(
                          paragraph ? styles.pr06 : `${styles.normalInput} pr-1`,
                          'd-flex flex-row align-items-center pl-0 mb-2',
                        )}
                      >
                        <Input
                          className={classNames(isExist ? styles.isInputErr : '', 'position-relative w-100')}
                          name={`${name}[${index}].name`}
                          onChange={(e: any) => {
                            checkDoubles();
                            onChange(e);
                          }}
                        />
                        <Col xs={2} className={styles.customCol} />

                        {index > 0 && (
                          <Button
                            variant="danger"
                            className={classNames(
                              styles.minus_btn,
                              paragraph ? styles.minus_btnParag : styles.minus_btnCen,
                              'rounded-circle mr-1',
                            )}
                            onClick={name.includes('securityGates') ? openDialog : onRemove}
                          >
                            <Minus size={14} />
                          </Button>
                        )}
                        {isExist && (
                          <h5 className={paragraph ? styles.errormsgPar : styles.errormsg}>Вже є у списку</h5>
                        )}
                      </div>
                    );
                  })}
              </div>
              <ModalWrapper
                show={isShow}
                onHide={() => {
                  setRemoveIndex(0);
                  closeModal();
                }}
              >
                <div className="d-flex flex-column">
                  <h6 className="text-center">Бажаєте видалити пост охорони?</h6>

                  <div className="d-flex justify-content-around mt-3">
                    <Button
                      className="px-5"
                      onClick={() => {
                        setRemoveIndex(0);
                        closeModal();
                      }}
                      variant="outline-secondary"
                    >
                      Закрити
                    </Button>
                    <Button className="px-5" onClick={() => removeHandler(removeIndex)} variant="outline-danger">
                      Підтвердити
                    </Button>
                  </div>
                </div>
              </ModalWrapper>
            </>
          );
        }}
      />
    </Form.Group>
  );
};

export default MultyInput;
